/* OwnPathPicker.jsx — Path #7 (Entrepreneurship · OwnPath).
 * User page = the state-first All-India explorer (28 states, JSON-driven, OwnPath v20).
 * Backend keep/lock endpoints in ownpath.py are untouched; the old budget+state picker UI was removed per user. */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ChevronLeft, Landmark, Store, FlaskConical } from 'lucide-react';
import Navbar from '../components/Navbar';
import PathwayDisclaimer from '../components/PathwayDisclaimer';
import StateExplorer from '../components/ownpath/StateExplorer';
import SpecIntroVideo, { introSeen, markIntroSeen } from '../components/SpecIntroVideo';
import { getIntroVideo } from '../data/introVideos';
import { useAuth } from '../lib/auth';

const INTRO_KEY = 'ownpath';

const PROMISES = [
  { icon: Landmark, title: 'Government support first', text: 'Verified state + national routes with honest status badges — every button opens the official source.' },
  { icon: Store, title: 'Local opportunities, not hype', text: 'Officially evidenced district products, crafts and food — shown as hypotheses to test, never as guaranteed income.' },
  { icon: FlaskConical, title: 'Seven-day paid test', text: 'Two quotations, one small pilot, real paying customers — then continue, change or stop.' },
];

export default function OwnPathPicker() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(false);
  const [introChecked, setIntroChecked] = useState(false);
  const deepLinkState = useRef(new URLSearchParams(window.location.search).get('state') || '');

  useEffect(() => {
    setShowIntro(!introSeen(INTRO_KEY, user));
    setIntroChecked(true);
  }, [user]);

  useEffect(() => {
    if (!deepLinkState.current || !introChecked || showIntro) return;
    const t = setTimeout(() => {
      document.querySelector('[data-testid="op-schemes-browser"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => clearTimeout(t);
  }, [introChecked, showIntro]);

  if (!introChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF9F1]">
        <Loader2 className="w-8 h-8 animate-spin text-rose-600" />
      </div>
    );
  }

  if (showIntro) {
    const v = getIntroVideo(INTRO_KEY);
    return <SpecIntroVideo videoId={v.videoId} eyebrow={v.eyebrow}
      onDone={() => { markIntroSeen(INTRO_KEY, user); setShowIntro(false); }} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      <Navbar />
      <div className="pt-24 md:pt-28 pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
            <button type="button" onClick={() => navigate('/dashboard')}
              className="inline-flex items-center text-sm font-semibold text-gray-600 hover:text-gray-900" data-testid="op-back-btn">
              <ChevronLeft size={16} className="mr-1" /> Back
            </button>
            <button type="button" onClick={() => setShowIntro(true)}
              className="inline-flex items-center gap-1 text-xs font-bold text-rose-700 hover:text-rose-900" data-testid="op-watch-intro-btn">
              ▶ Watch intro
            </button>
          </div>

          <div className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-rose-700 mb-2">
              Entrepreneurship · OwnPath · Built for all over India
            </p>
            <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 max-w-3xl mx-auto leading-tight" data-testid="op-heading">
              Start your own. Stand on your own feet.
            </h1>
            <p className="text-sm md:text-base text-gray-600 mt-3 max-w-2xl mx-auto">
              Pick your state to see verified government support first, then locally evidenced business opportunities, official learning routes and a seven-day test plan.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mb-8" data-testid="op-promise-strip">
            {PROMISES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="rounded-2xl border border-rose-100 bg-white/80 backdrop-blur px-4 py-3 flex gap-3">
                <div className="w-9 h-9 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-700 shrink-0"><Icon size={16} /></div>
                <div><p className="text-sm font-bold text-gray-900 leading-tight">{title}</p><p className="text-xs text-gray-600 mt-0.5 leading-snug">{text}</p></div>
              </div>
            ))}
          </div>

          <div data-testid="op-schemes-browser">
            <StateExplorer initialState={deepLinkState.current} />
          </div>
        </div>
      </div>
      <PathwayDisclaimer />
    </div>
  );
}
